import { scaleLinear, ScaleLinear } from 'd3';
import { JourneyMap as JourneyMapProto } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { int64 } from '@fullstory/proto/google/protobuf';
import {
  directionEnumToString,
  GraphLink,
  GraphNode,
  JourneyMapSessionFilter,
  RevolveLabel,
} from './types';
import { BOX_HEIGHT, BOX_WIDTH, LINK_LENGTH, OtherNodeIdentifier } from './const';
import { JourneyMapSettings } from '@fullstory/proto/fs/services/search-settings/visualizationsettings';
import { cloneDeep, flatten, groupBy, isEqual, mapValues, remove } from 'lodash';
import { isNonNull } from '@fullstory/typeutils';
import { DependentEventFilter } from '@fullstory/proto/fs/api/segment';
import { OTHER_LABEL } from '@fullstory/library-ui';

export const generateScaleFunction = (
  totalSourceCount: number,
  minHeight = 1,
  maxHeight = BOX_HEIGHT,
): ScaleLinear<number, number> =>
  scaleLinear().domain([0, totalSourceCount]).range([minHeight, maxHeight]);

export const scaleLinks = (
  links: GraphLink[],
  scaleFunc: ScaleLinear<number, number>,
): GraphLink[] =>
  links.map(l => ({
    ...l,
    height: scaleFunc(l.value),
  }));

export const getNodeHash = (h: string, d: number) => `${h}-${d}`;
type JourneyMapDataCollection = JourneyMapProto.Pivot[] | JourneyMapProto.Interaction[];

export const getYPos = (index: number) => 84 + (BOX_HEIGHT + 25) * index;

const draftGraphNode = (
  hash: string,
  depth: number,
  position: { x: number; y: number },
): GraphNode =>
  ({
    ...position,
    identHash: hash,
    depth,
    getId() {
      return getNodeHash(this.identHash.toString(), this.depth);
    },
  } as GraphNode);

const makeIdentHash = (data?: JourneyMapProto.InteractionData | null) =>
  data?.identHash || (data?.other ? OTHER_LABEL : undefined);

export const buildJourneyMap = (
  jm: JourneyMapProto,
  direction: JourneyMapSettings.Direction,
  filters?: { [key: number]: JourneyMapSessionFilter },
  /**
   * This is a hack that enables us to exit out of some processes early while still having the info to post process
   * 'ends with' journeys. This number comes from having processed the entire journey in an earlier call.
   */
  knownMaxDepth?: number,
): { nodes: GraphNode[]; links: GraphLink[] } => {
  const isForward = direction === JourneyMapSettings.Direction.DIRECTION_FORWARD;
  // if we have any filters, apply them to the pivots
  const pivotCollection = jm.pivots
    .map((p: JourneyMapProto.Pivot) => p)
    .filter(p => {
      const identHash = makeIdentHash(p.data);
      if (filters?.[0]?.hash && filters[0].hash !== identHash && filters[0].hash !== 'any')
        return false;
      return true;
    });

  if (filters) {
    // Can a selected filter reach another selected filter? If not, exit this whole thing early
    const filterKeys = Object.keys(filters)
      .map(k => parseInt(k, 10))
      .filter(
        k =>
          filters[k]?.hash !== 'any' &&
          filters[k]?.hash !== RevolveLabel.EXIT &&
          filters[k]?.hash !== RevolveLabel.ENTRY,
      )
      .sort();
    for (let i = 0; i < filterKeys.length; i++) {
      const filterKey = filterKeys[i];
      if (!filterKey) continue;
      const filter = filters[filterKey];
      if (!filter) continue;
      for (let j = i + 1; j < filterKeys.length; j++) {
        const otherFilterKey = filterKeys[j];
        if (!otherFilterKey) continue;
        const otherFilter = filters[otherFilterKey];
        if (!otherFilter) continue;

        const canProcess = isInteractionInPath(
          { hash: filter.hash, depth: filterKey },
          { hash: otherFilter.hash, depth: otherFilterKey },
          jm.pivots,
        );
        if (!canProcess) {
          return { nodes: [], links: [] };
        }
      }
    }
  }

  let currentCollection: JourneyMapDataCollection = pivotCollection;
  let depth = 0;
  const nodes: GraphNode[] = [];
  const links: GraphLink[] = [];
  const incomingCounts: { [key: string]: number } = {};

  // Get counts for all pivots first
  for (let i = 0; i < currentCollection.length; i++) {
    const pivot = currentCollection[i];
    const identHash = makeIdentHash(pivot?.data);
    if (!identHash) continue;
    incomingCounts[getNodeHash(identHash.toString(), depth)] = int64.toNumber(pivot?.data?.count);
  }
  const otherCountsByDepth = jm.pivotOthers ? getOtherCounts(jm.pivotOthers) : null;

  // keep track of previous collection so we can link source and target nodes
  let previousCollection: JourneyMapDataCollection = [];

  // go through the journey map breadth first
  while (currentCollection.length) {
    if (filters) {
      // we can stop at the first entry or exit
      if (
        filters[depth]?.hash &&
        (filters[depth]?.hash === RevolveLabel.EXIT || filters[depth]?.hash === RevolveLabel.ENTRY)
      ) {
        break;
      }
    }
    const nextCollection: JourneyMapProto.Interaction[] = [];
    let yMultiple = 0;
    for (let i = 0; i < currentCollection.length; i++) {
      const interaction = currentCollection[i];

      const identHash = makeIdentHash(interaction?.data);

      // can happen if we don't request event identities. how would we proceed?
      if (!identHash) continue;

      if (filters) {
        // we don't need to process a node at this depth if the filter doesn't match
        if (
          filters[depth]?.hash &&
          filters[depth]?.hash !== 'any' &&
          filters[depth]?.hash !== identHash
        ) {
          continue;
        }

        // Try to find if there is any path from this node to all the filters, otherwise we exclude it.
        // This interaction must be reachable through the selected path.
        const filterKeys = Object.keys(filters).map(k => parseInt(k, 10));
        let canProcess = true;
        for (let k = 0; k < filterKeys.length; k++) {
          const filterKey = filterKeys[k];
          if (!filterKey) continue;
          const filter = filters[filterKey];
          if (
            !filter ||
            filter.hash === 'any' ||
            filter.hash === RevolveLabel.EXIT ||
            filter.hash === RevolveLabel.ENTRY
          )
            continue;
          canProcess = isInteractionInPath(
            { hash: identHash.toString(), depth },
            { hash: filter.hash, depth: filterKey },
            jm.pivots,
          );

          if (!canProcess) {
            break;
          }
        }
        if (!canProcess) continue;
      }

      const node: GraphNode = draftGraphNode(identHash.toString(), depth, {
        x: 32 + (isForward ? (BOX_WIDTH + LINK_LENGTH) * depth : 0),
        y: getYPos(yMultiple),
      });

      const countsHash = node.getId();
      const count = incomingCounts[countsHash];
      if (count) {
        // we've counted all the incoming counts from the last iteration
        node.count = count;
      }

      // queue up all the children for the next iteration and
      // aggregate the flow of counts into the next set of nodes
      const next =
        (interaction as JourneyMapProto.Pivot)[directionEnumToString(direction)] ??
        (interaction as JourneyMapProto.Interaction).next;
      let nextCount = 0;
      for (let k = 0; k < next.length; k++) {
        const n = next[k];

        const identHash = makeIdentHash(n?.data);
        if (!n || !identHash) continue;

        const nextCountsHash = getNodeHash(identHash.toString(), depth + 1);
        // the counts on a node are the sum of all incoming counts from the previous nodes
        incomingCounts[nextCountsHash] =
          (incomingCounts[nextCountsHash] ?? 0) + int64.toNumber(n.data?.count);

        nextCount += int64.toNumber(n.data?.count);

        nextCollection.push(n);
      }

      node.nextCount = nextCount;

      // If "Other" node, add other count
      if (node.identHash === OTHER_LABEL && otherCountsByDepth) {
        node.otherCount = otherCountsByDepth[depth];
      }

      // if the node exists, update it in the list
      const existingNodeIndex = nodes.findIndex(n => n.getId() === node.getId());
      const existingNode = nodes[existingNodeIndex];
      if (existingNode) {
        existingNode.nextCount += node.nextCount;
      } else {
        nodes.push(node);
        yMultiple++;
      }
    }

    // build up links for the nodes we just made
    links.push(...buildLinks(previousCollection, nodes, direction, depth));

    depth = depth + 1;
    previousCollection = currentCollection.slice();
    currentCollection = nextCollection;

    // for journey maps going backward from the pivot, the pivot will be the furthest out (x coordinate)
    // since we don't know how deep this journey map is yet, push the pivot x coordinate back at each iteration
    if (currentCollection.length && !isForward) {
      nodes.forEach((n: GraphNode) => (n.x += BOX_WIDTH + LINK_LENGTH));
    }
  }

  !isForward &&
    nodes.forEach(
      // since we chopped off the last node when `omitLastStep == true` we compensate when flipping all the depths
      // it's convoluted so it'd be nice to change `depth` to `distanceFromPivot` instead (TODO)
      (n: GraphNode) => (n.depth = Math.abs(n.depth - (knownMaxDepth ?? depth) + 1)),
    );

  sortAndRepositionNodes(nodes);

  if (filters) {
    pruneSelectedGraph(nodes, links, filters, pivotCollection, isForward, knownMaxDepth ?? depth);
  }

  return { nodes, links };
};

//
/**
 *
 * @param a An endpoint in the journey
 * @param b An endpoint in the journey
 * @param pivots Always start from the pivot and build everything leading up to an endpoint because there may be a path
 *  from one endpoint to another when starting at some pivots but not others.
 * @returns true or false after a BFS for the deeper endpoint. If we can get from one point to another then return true.
 */
export function isInteractionInPath(
  a: { hash: string; depth: number },
  b: { hash: string; depth: number },
  pivots: JourneyMapProto.Pivot[],
): boolean {
  if (isEqual(a, b)) return true;
  let source = a;
  let sink = b;
  if (a.depth > b.depth) {
    // Always search in one direction
    const tmp = cloneDeep(sink);
    sink = source;
    source = tmp;
  }
  // build up starting point based on source
  let sourceInteractions: JourneyMapProto.Interaction[] = pivots.map(pivot => ({
    ...pivot,
    next: [...pivot.forward, ...pivot.backward],
  }));
  let currentDepth = 0;
  while (currentDepth != source.depth) {
    const next = flatten(sourceInteractions.map(i => i.next));
    sourceInteractions = next;
    currentDepth = currentDepth + 1;
  }

  const foundSources = sourceInteractions.filter(i => makeIdentHash(i?.data) === source.hash);
  if (!foundSources.length) return false;
  sourceInteractions = [...foundSources];

  // perform the search
  while (currentDepth != sink.depth) {
    const next = flatten(sourceInteractions.map(i => i.next));
    sourceInteractions = next;
    currentDepth = currentDepth + 1;
  }

  if (
    sourceInteractions.find(
      i => makeIdentHash(i?.data) === sink.hash && currentDepth === sink.depth,
    )
  )
    return true;

  return false;
}

function getOtherCounts(pivotOthers: JourneyMapProto.PivotOthers): { [key: number]: number } {
  const otherCounts: { [key: number]: number } = { 0: pivotOthers.top.length };
  let currentDepth = 1;
  let next = pivotOthers.forward ?? pivotOthers.backward;
  while (next) {
    otherCounts[currentDepth] = next.top.length;
    next = next.next;
    currentDepth += 1;
  }
  return otherCounts;
}

const getLinkHash = (source: GraphNode, target: GraphNode): string =>
  `${source.getId()}-${target.getId()}`;

function buildLinks(
  sources: JourneyMapDataCollection,
  nodes: GraphNode[],
  direction: JourneyMapSettings.Direction,
  depth: number,
): GraphLink[] {
  const links: { [key: string]: GraphLink } = {};
  // for a given node we've previously processed, use it as a source node to build links to all of its children
  for (let i = 0; i < sources.length; i++) {
    const source = sources[i];
    // find source node
    let sourceNode: GraphNode | undefined = undefined;
    for (let n = 0; n < nodes.length; n++) {
      const node = nodes[n];
      const identity =
        source?.data &&
        (source.data.identHash || (source.data.other ? OtherNodeIdentifier : undefined));
      if (node?.depth === depth - 1 && node.identHash === identity?.toString()) {
        sourceNode = node;
        break;
      }
    }
    if (!sourceNode) continue;
    // find all the targets
    const next =
      (source as JourneyMapProto.Pivot)[directionEnumToString(direction)] ??
      (source as JourneyMapProto.Interaction).next;
    for (let k = 0; k < next.length; k++) {
      const target = next[k];
      let targetNode: GraphNode | undefined = undefined;
      for (let n = 0; n < nodes.length; n++) {
        const node = nodes[n];
        const identity =
          target?.data &&
          (target.data.identHash || (target.data.other ? OtherNodeIdentifier : undefined));
        if (node?.depth === depth && node.identHash === identity?.toString()) {
          targetNode = node;
          break;
        }
      }
      if (!targetNode) continue;

      const newLink = {
        value: int64.toNumber(target?.data?.count),
        source: sourceNode,
        target: targetNode,
        height: (int64.toNumber(target?.data?.count) / sourceNode.nextCount) * 100,
        getId() {
          return getLinkHash(this.source, this.target);
        },
      };

      const linkHash = getLinkHash(sourceNode, targetNode);
      const link = links[linkHash];
      if (link) {
        newLink.value += link.value;
        newLink.height = (newLink.value / sourceNode.nextCount) * 100;
      }

      if (direction === JourneyMapSettings.Direction.DIRECTION_BACKWARD) {
        // flip source and target
        newLink.source = targetNode;
        newLink.target = sourceNode;
      }

      links[linkHash] = newLink;
    }
  }

  return Object.keys(links)
    .map(k => links[k])
    .filter(isNonNull);
}

export const buildInteractionSessionColumn = (
  nodes: GraphNode[],
  offset: number,
): {
  [x: string]: {
    x: number;
    continue: number;
    exit: number;
    total: number;
  }[];
} =>
  mapValues(groupBy(nodes, 'depth'), (nodes: GraphNode[]) => [
    {
      x: (nodes[0]?.x ?? 0) + offset,
      continue: nodes.reduce((total, node) => total + node.nextCount, 0),
      exit: nodes.reduce((total, node) => total + (node.count - node.nextCount), 0),
      total: nodes.reduce((total, node) => total + node.count, 0),
    },
  ]);
export const calculateXMultiple = (xMultiple: number): number =>
  (BOX_WIDTH + LINK_LENGTH) * xMultiple + 32;

/**
 *
 * @param nodes The nodes to sort
 * @returns void
 * Sorts the nodes by count in descending order within the nodes' depth, with the "Other" node as the last node in that list,
 * then reposition the nodes along the y-axis. This modifies the original array.
 */
function sortAndRepositionNodes(nodes: GraphNode[]) {
  if (!nodes.length || nodes.length === 1) return;

  const nodesByDepth = groupBy(nodes, n => n.depth);
  const depths = Object.keys(nodesByDepth);
  for (const depth of depths) {
    const nodesAtDepth = nodesByDepth[depth];

    if (!nodesAtDepth || nodesAtDepth.length <= 1) continue;

    const sortedNodes = nodesAtDepth.sort((a, b) => {
      if (a.identHash === OtherNodeIdentifier) return 1;
      if (b.identHash === OtherNodeIdentifier) return -1;
      return b.count - a.count;
    });

    sortedNodes.forEach((n, y) => (n.y = getYPos(y)));
  }
}

/**
 *
 * @param nodes The selected graph nodes
 * @param links The selected graph links
 * @param filters Any filters applied to generate the selected graph
 * @returns void
 * Prunes the selected journey graph. Ending at the last filter depth, excluding any filters on Entry/Exit/Any,
 * we look back and remove any nodes that do not have a target pair in any link. This modifies the original graph.
 */
function pruneSelectedGraph(
  nodes: GraphNode[],
  links: GraphLink[],
  filters: { [key: number]: JourneyMapSessionFilter },
  pivotCollection: JourneyMapProto.Pivot[],
  isForward: boolean,
  knownMaxDepth: number,
) {
  const validFilterPaths = findFilterValidPaths(pivotCollection, filters);

  const depths = Object.keys(filters).map(k => parseInt(k, 10));
  const maxFilterDepth = Math.max(...depths);

  // Iterate through the nodes to check which ones we need to prune since the full path would be invalid (no session at end).
  for (const testNode of [...nodes]) {
    // Add or replace the filter's node at testNode's depth to determine if it could be valid
    const potentialFilters = { ...filters };

    let adjustedDepth = testNode.depth;
    if (!isForward) {
      // nodes' depths are reversed from calling function for ends with journeys. Adjust depth to match filter depth.
      adjustedDepth = knownMaxDepth - testNode.depth - 1;
    }

    potentialFilters[adjustedDepth] = {
      hash: testNode.identHash,
      values: null, // not used in this context
    };

    // Any node passed into this function, that's after the last filter item, is valid
    let validPathFound = adjustedDepth > maxFilterDepth;
    if (!validPathFound) {
      // Try to find one valid path that has this node at its location
      for (const validPath of validFilterPaths) {
        const nodeAtDepth = validPath[adjustedDepth];

        if (
          nodeAtDepth?.identHash === testNode.identHash ||
          (nodeAtDepth?.other && testNode.identHash === OtherNodeIdentifier)
        ) {
          validPathFound = true;
          break;
        }
      }
    }

    if (!validPathFound) {
      remove(nodes, n => n.getId() === testNode.getId());
      // also remove any links where this node is at a target site
      const linkAtTarget = links.find(l => l.target.getId() === testNode.getId());
      if (linkAtTarget) {
        remove(links, l => l.getId() === linkAtTarget.getId());
      }
    }
  }
}

/**
 * Returns valid paths defined by the filter. If the filter doesn't go to the end, this will only return the path
 * up until the last node in the filter. Anything that follows any of these paths would be valid.
 */
function findFilterValidPaths(
  pivots: JourneyMapProto.Pivot[],
  filters: { [key: number]: JourneyMapSessionFilter },
) {
  const depths = Object.keys(filters).map(k => parseInt(k, 10));
  const maxDepth = Math.max(...depths);

  let allValidPaths: InteractionsByDepth[] = [];

  for (const pivot of pivots) {
    const path: { [key: number]: JourneyMapProto.InteractionData } = {
      0: pivot.data!,
    };

    const validPathsForPivot = [];

    for (const interaction of pivot.backward) {
      const potentialPath = { ...path };

      // Every path after this is valid
      if (maxDepth === 0) {
        validPathsForPivot.push(potentialPath);
      }

      const validPaths = findValidPathForFilter(
        interaction,
        filters,
        1,
        maxDepth,
        potentialPath,
        validPathsForPivot,
      );
      allValidPaths = allValidPaths.concat(validPaths);
    }
    for (const interaction of pivot.forward) {
      const potentialPath = { ...path };

      // Every path after this is valid
      if (maxDepth === 0) {
        validPathsForPivot.push(potentialPath);
      }

      const validPaths = findValidPathForFilter(
        interaction,
        filters,
        1,
        maxDepth,
        { ...path },
        validPathsForPivot,
      );
      allValidPaths = allValidPaths.concat(validPaths);
    }
  }

  return allValidPaths;
}

type InteractionsByDepth = { [key: number]: JourneyMapProto.InteractionData };

/**
 * Recursively find all valid paths for the given filter.
 */
function findValidPathForFilter(
  interaction: JourneyMapProto.Interaction,
  filters: {
    [key: number]: JourneyMapSessionFilter;
  },
  depth: number,
  filterMaxDepth: number,
  potentialPath: InteractionsByDepth,
  validPaths: InteractionsByDepth[] = [],
): { [key: number]: JourneyMapProto.InteractionData }[] {
  if (depth > filterMaxDepth) {
    return validPaths;
  }

  // "Other" can't be selected for a filter so ignore it
  if (!interaction?.data?.identHash && !interaction?.data?.other) {
    return validPaths;
  }

  const nodeToMatch = filters[depth];

  if (nodeToMatch && nodeToMatch.hash !== interaction.data.identHash) {
    return validPaths;
  }

  potentialPath[depth] = interaction.data;

  // If we're at the last depth, that's defined by the filter, make final decision if this is a valid path
  if (depth == filterMaxDepth) {
    if (interaction.data.identHash === nodeToMatch?.hash) {
      validPaths.push(potentialPath);
      return validPaths;
    } else {
      return validPaths;
    }
  }

  for (const nextInteraction of interaction.next) {
    findValidPathForFilter(
      nextInteraction,
      filters,
      depth + 1,
      filterMaxDepth,
      { ...potentialPath },
      validPaths,
    );
  }

  return validPaths;
}

export const interactionDirectionOffset = (
  direction: JourneyMapSettings.Direction,
  canAppendInteraction: boolean,
): number =>
  canAppendInteraction && direction === JourneyMapSettings.Direction.DIRECTION_BACKWARD
    ? BOX_WIDTH
    : 0;

export const generateGetBoundingClientRect =
  (x: number = 0, y: number = 0): (() => DOMRect) =>
  () => ({
    width: 0,
    height: 0,
    top: y,
    right: x,
    bottom: y,
    left: x,
    x,
    y,
    toJSON() {
      return JSON.stringify(this, (key, value) => (key === 'toJSON' ? undefined : value));
    },
  });

export const getVarSublabel = (dep: DependentEventFilter): [string, string] | null => {
  const oneof = DependentEventFilter.filter_oneof(dep);
  if (!oneof) {
    return null;
  }
  switch (oneof.opt) {
    case DependentEventFilter.filter.customEventPropertyBool:
    case DependentEventFilter.filter.customEventPropertyBools:
    case DependentEventFilter.filter.customEventPropertyFloat:
    case DependentEventFilter.filter.customEventPropertyFloats:
    case DependentEventFilter.filter.customEventPropertyInt:
    case DependentEventFilter.filter.customEventPropertyInts:
    case DependentEventFilter.filter.customEventPropertyString:
    case DependentEventFilter.filter.customEventPropertyStrings:
    case DependentEventFilter.filter.pageVarBool:
    case DependentEventFilter.filter.pageVarBools:
    case DependentEventFilter.filter.pageVarFloat:
    case DependentEventFilter.filter.pageVarFloats:
    case DependentEventFilter.filter.pageVarInt:
    case DependentEventFilter.filter.pageVarInts:
    case DependentEventFilter.filter.pageVarString:
    case DependentEventFilter.filter.pageVarStrings:
    case DependentEventFilter.filter.elemVarBool:
    case DependentEventFilter.filter.elemVarBools:
    case DependentEventFilter.filter.elemVarFloat:
    case DependentEventFilter.filter.elemVarFloats:
    case DependentEventFilter.filter.elemVarInt:
    case DependentEventFilter.filter.elemVarInts:
    case DependentEventFilter.filter.elemVarString:
    case DependentEventFilter.filter.elemVarStrings:
      return [oneof.val.equals?.name ?? '', oneof.val.equals?.value?.toString() ?? ''];
    default:
      return null;
  }
};
